import * as React from 'react';
import { useState } from 'react';
import useForkRef from '@mui/utils/useForkRef';
import {
  DatePicker,
  type DatePickerProps,
} from '@mui/x-date-pickers/DatePicker';
import { Box, InputAdornment, useMediaQuery } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import dayjs, { Dayjs } from '@/config/dayjs';
import {
  DateValidationError,
  PickerChangeHandlerContext,
} from '@mui/x-date-pickers';
import {
  PickersShortcutsItemContext,
  PickersShortcutsProps,
} from '@mui/x-date-pickers/PickersShortcuts';
import type { DateFieldProps } from '@mui/x-date-pickers/DateField';
import { FieldType } from '@mui/x-date-pickers-pro/models';
import DateRangeIcon from '@mui/icons-material/DateRange';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import {
  mobileWidth,
  mobileWidthValue,
  pickerDesktopModeMediaQuery,
} from '@/constants';
import { isCapacitorApp } from '@/utils/isCapacitorApp';
import PickerShortcutsSelect from './PickerShortcutsSelect';
import type { SingleDateShortcut } from './asOfDate';
import { EMBEDDABLE_PICKER_DESKTOP_MODE_MEDIA_QUERY } from '@/integrations/embeddableWidgetPortalUtils';
import { useWidgetPortalContainer } from '@/integrations/embeddableWidgetPortalContext';
import { useEmbeddablePickerPortalSlotProps } from '@/integrations/useEmbeddablePickerPortalSlotProps';
import { usePickerContext } from '@mui/x-date-pickers/hooks';
import { pageHeaderDatePickerTextFieldSx } from './sharedDatePickerSx';
import { StyledIconButton } from '../common/StyledIconButton';

interface MobilePrimaryIconSingleDateFieldProps extends DateFieldProps {
  setOpen?: React.Dispatch<React.SetStateAction<boolean>>;
}

type MobilePrimaryIconSingleDateFieldComponent = ((
  props: MobilePrimaryIconSingleDateFieldProps &
    React.RefAttributes<HTMLDivElement>,
) => React.JSX.Element) & { fieldType?: FieldType };

const MobilePrimaryIconSingleDateField = React.forwardRef(
  function MobilePrimaryIconSingleDateField(
    props: MobilePrimaryIconSingleDateFieldProps,
    ref: React.Ref<HTMLDivElement>,
  ) {
    const {
      setOpen,
      label,
      disabled,
      id,
      inputProps: { 'aria-label': ariaLabel } = {},
    } = props;

    const { rootRef, triggerRef } = usePickerContext();
    const handleRootRef = useForkRef(rootRef, ref);

    return (
      <Box
        ref={handleRootRef}
        sx={{
          display: 'flex',
          justifyContent: 'flex-start',
          alignItems: 'center',
          width: '100%',
        }}
      >
        <StyledIconButton
          ref={triggerRef}
          color='primary'
          size='medium'
          disabled={disabled}
          id={id}
          type='button'
          aria-label={
            typeof ariaLabel === 'string' && ariaLabel
              ? ariaLabel
              : typeof label === 'string'
                ? label
                : undefined
          }
          onClick={() => !disabled && setOpen?.(true)}
        >
          <CalendarMonthIcon />
        </StyledIconButton>
      </Box>
    );
  },
) as unknown as MobilePrimaryIconSingleDateFieldComponent;

MobilePrimaryIconSingleDateField.fieldType = 'single-input';

interface SingleDateProps {
  date?: string;
  onChange?: (date: string, shortcutLabel?: string) => void;
  label?: string;
  showShortcuts?: boolean;
  /** Presets in place of the general ones, such as the as-of date's. */
  shortcuts?: readonly SingleDateShortcut[];
  /** The earliest pickable day; presets before it are not offered. */
  minDate?: Dayjs;
  maxDate?: Dayjs;
  disabled?: boolean;
  useMobilePrimaryIconButton?: boolean;
  /**
   * When true (narrow dashboard tile / widget container), use the primary outlined icon field
   * like the mobile header/flyout, independent of viewport width.
   */
  useWidgetNarrowIconField?: boolean;
}

/** The general presets, for a date that is not an as-of date. */
const DATE_SHORTCUTS: readonly SingleDateShortcut[] = [
  {
    label: 'Today',
    getValue: () => dayjs(),
  },
  {
    label: 'Yesterday',
    getValue: () => dayjs().subtract(1, 'day'),
  },
  {
    label: 'Last Week',
    getValue: () => dayjs().subtract(1, 'week'),
  },
  {
    label: 'Last Month',
    getValue: () => dayjs().subtract(1, 'month'),
  },
  {
    label: 'Last Quarter',
    getValue: () => {
      const currentDate = dayjs();
      const currentQuarter = Math.floor(currentDate.month() / 3);
      const previousQuarter = currentQuarter === 0 ? 3 : currentQuarter - 1;
      const year =
        previousQuarter === 3 ? currentDate.year() - 1 : currentDate.year();
      return dayjs(new Date(year, previousQuarter * 3, 1));
    },
  },
  {
    label: 'Last 3 Months',
    getValue: () => dayjs().subtract(3, 'month'),
  },
  {
    label: 'Last 6 Months',
    getValue: () => dayjs().subtract(6, 'month'),
  },
  {
    label: 'Last Year',
    getValue: () => dayjs().subtract(1, 'year'),
  },
];

export default function SingleDate({
  date,
  onChange,
  label = 'Date',
  showShortcuts = true,
  shortcuts = DATE_SHORTCUTS,
  minDate,
  maxDate,
  disabled,
  useMobilePrimaryIconButton = false,
  useWidgetNarrowIconField = false,
}: SingleDateProps) {
  const embeddablePortalSlotProps = useEmbeddablePickerPortalSlotProps();
  const widgetPortalContainer = useWidgetPortalContainer();
  const isMobileWidth = useMediaQuery(mobileWidth);
  const useIconButtonMobile =
    (useMobilePrimaryIconButton && isMobileWidth) || useWidgetNarrowIconField;
  const value = (() => {
    if (!date) return null;
    const strict = dayjs(date, 'MM/DD/YYYY', true);
    if (strict.isValid()) return strict;
    const parsed = dayjs(date);
    return parsed.isValid() ? parsed : null;
  })();

  const today = dayjs();
  const effectiveMaxDate =
    maxDate && maxDate.isBefore(today, 'day') ? maxDate : today;
  const offeredShortcuts = shortcuts.filter(
    (shortcut) => !minDate || !shortcut.getValue().isBefore(minDate, 'day'),
  );

  const [lastShortcutSelected, setLastShortcutSelected] = useState<
    PickersShortcutsItemContext | undefined
  >(undefined);
  const [open, setOpen] = useState(false);

  const handleChange = (
    value: dayjs.Dayjs | null,
    context: PickerChangeHandlerContext<DateValidationError>,
  ) => {
    setLastShortcutSelected(context.shortcut);
    onChange?.(value?.format('MM/DD/YYYY') || '', context.shortcut?.label);
  };

  const MobileShortcuts = (
    props: PickersShortcutsProps<dayjs.Dayjs | null>,
  ) => (
    <PickerShortcutsSelect
      items={props.items}
      placeholder={label}
      selectedLabel={lastShortcutSelected?.label}
      onSelect={(item) => {
        const nextValue = item.getValue({ isValid: () => true });
        if (!nextValue) return;

        setLastShortcutSelected(item);
        onChange?.(nextValue.format('MM/DD/YYYY'), item.label);
        setOpen(false);
      }}
    />
  );

  const datePickerSlotProps = {
    shortcuts: showShortcuts
      ? {
          items: offeredShortcuts,
        }
      : undefined,
    ...(useIconButtonMobile
      ? {
          field: { setOpen },
        }
      : {
          textField: {
            label: lastShortcutSelected?.label,
            disabled,
            sx: pageHeaderDatePickerTextFieldSx,
            inputProps: isCapacitorApp() ? { readOnly: true } : undefined,
            InputProps: {
              endAdornment: (
                <InputAdornment
                  position='end'
                  onClick={() => {
                    if (!disabled) setOpen(true);
                  }}
                  sx={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
                >
                  <DateRangeIcon />
                </InputAdornment>
              ),
            },
          },
        }),
    actionBar: {
      actions: ['clear', 'cancel', 'accept'] as const,
      sx: {
        justifyContent: 'center',
      },
    },
    ...embeddablePortalSlotProps,
    dialog: {
      ...embeddablePortalSlotProps.dialog,
      sx: (theme: Theme) => ({
        '& .MuiDialog-container .MuiPaper-root': {
          boxSizing: 'border-box',
          padding: '4vh 4px !important',
        },
        [theme.breakpoints.down(mobileWidthValue)]: {
          '& .MuiDialog-container .MuiPaper-root': {
            width: '100% !important',
            maxWidth: '100% !important',
            display: 'block !important',
            margin: 0,
            position: 'absolute',
            top: 0,
            left: 0,
            height: '100% !important',
            maxHeight: 'none !important',
            boxSizing: 'border-box',
            padding: '70px 0px 0px !important',
          },
          '& .MuiPickersLayout-root': {
            display: 'block !important',
          },
          '& .MuiDialogActions-root': {
            paddingTop: '0px !important',
            paddingBottom: '0px !important',
          },
          '& .MuiList-padding': {
            display: 'block !important',
            width: '100% !important',
            position: 'relative !important',
            maxWidth: '100% !important',
            maxHeight: 'auto !important',
            boxSizing: 'border-box !important',
            padding: '0px 20px 40px !important',
            textAlign: 'center',
          },
          '& .MuiListItem-root': {
            display: 'inline-block !important',
            width: 'auto !important',
            padding: '6px !important',
          },
        },
      }),
    },
  } as unknown as NonNullable<DatePickerProps['slotProps']>;

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: useIconButtonMobile ? 'none' : '170px',
      }}
    >
      <DatePicker
        label={label}
        format='MM/DD/YYYY'
        value={value}
        onChange={handleChange}
        minDate={minDate}
        maxDate={effectiveMaxDate}
        disableFuture
        disabled={disabled}
        enableAccessibleFieldDOMStructure={!isCapacitorApp()}
        desktopModeMediaQuery={
          widgetPortalContainer
            ? EMBEDDABLE_PICKER_DESKTOP_MODE_MEDIA_QUERY
            : pickerDesktopModeMediaQuery
        }
        open={open}
        onOpen={() => {
          if (!disabled) setOpen(true);
        }}
        onClose={() => {
          setOpen(false);
        }}
        slots={{
          ...(useIconButtonMobile
            ? { field: MobilePrimaryIconSingleDateField }
            : {}),
          shortcuts:
            useIconButtonMobile && showShortcuts ? MobileShortcuts : undefined,
          toolbar: () => null,
        }}
        slotProps={datePickerSlotProps}
      />
    </Box>
  );
}
